import { Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Component to protect routes that require authentication.
// requiredCapability: if set, the user's JWT capabilities must include this flag.
// requiredFeature: if set, the org's feature toggle (settings.features.<key>) must not be
//   explicitly false — a visibility gate so a disabled feature's route isn't reachable by URL.
//   Defaults to ALLOW for a missing key (matches the UI's default-on semantics).
// Capability checks are defined centrally in App.js — do NOT add useEffect guards in individual pages.
function ProtectedRoute({ children, requiredRole = null, requiredCapability = null, requiredFeature = null }) {
    const { user, loading, isAuthenticated, checkPermission, checkFeature, organisationLoading } = useAuth();

    // Still loading initial auth check
    if (loading) {
        return <div>Loading Authentication...</div>;
    }

    // Wait for the org profile before evaluating a feature gate, so a gated page never
    // flashes before settings.features resolves (then redirects).
    if (requiredFeature && organisationLoading) {
        return <div>Loading...</div>;
    }

    // Not authenticated at all
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Check specific role requirements
    if (requiredRole === "danceSchool" && !user?.organisationId) {
        return <Navigate to="/login" replace />;
    }

    // Check permission-based requirements. Gated on the deliberate per-account
    // permissions.canUploadEvents flag on the JWT only.
    // Legacy org-role fallback (owner/admin of the org-context session) is parked
    // below — Uploads should be reachable only on an explicit flag, not by org role.
    // Keep in sync with the canShowUploads condition in Dashboard.js.
    if (requiredRole === "canUploadEvents") {
        const canUpload =
            user?.permissions?.canUploadEvents === true;
            // || (user?.loginType === "orgContext" && (user?.role === "owner" || user?.role === "admin"));
        if (!canUpload) {
            return <Navigate to="/login" replace />;
        }
    }

    // Check fine-grained capability from JWT (role: owner / admin / helper)
    // Redirects to /classes — safe landing for all org users regardless of role
    if (requiredCapability && !checkPermission(requiredCapability)) {
        return <Navigate to="/classes" replace />;
    }

    // Feature-toggle visibility gate. Only an EXPLICIT false blocks the route — a missing
    // key (or still-loading org) defaults to allowed. Redirects to the dashboard.
    if (requiredFeature && !checkFeature(requiredFeature)) {
        return <Navigate to="/dashboard" replace />;
    }

    // If authenticated and has proper role, render the protected component
    return children;
}

export default ProtectedRoute;
